import { useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useCopy, useFramework } from "../framework";

/**
 * The persistent section rail below the global header. Membership and order
 * are governed route metadata (`sectionNavigation` on each route record): the
 * first placement names a route's owning section, later placements cross-list
 * it in another section's rail. The rail stays sticky at every supported
 * width, marks the current route, and keeps hash targets clear of the sticky
 * offset by stamping the live rail height into the document scroll padding.
 */

function matchLength(routePath: string, pathname: string): number {
  if (routePath === "/") return pathname === "/" ? 1 : 0;
  if (pathname === routePath || pathname.startsWith(`${routePath}/`)) return routePath.length;
  return 0;
}

/**
 * Resolves the owning section for a pathname: the longest-matching route's
 * first `sectionNavigation` placement. The top nav and the rail both read
 * this, so the two "you are here" signals cannot disagree.
 */
export function owningSectionId(
  routes: ReadonlyArray<{ path: string; sectionNavigation?: ReadonlyArray<{ sectionId: string }> }>,
  pathname: string,
): string | undefined {
  const activeRoute = routes.reduce<{ length: number; route: (typeof routes)[number] | undefined }>(
    (best, route) => {
      const length = matchLength(route.path, pathname);
      return length > best.length ? { length, route } : best;
    },
    { length: 0, route: undefined },
  ).route;
  return activeRoute?.sectionNavigation?.[0]?.sectionId;
}

export function SectionNavigation() {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const location = useLocation();
  const railRef = useRef<HTMLElement>(null);
  const routes = snapshot.content.routes;

  const sectionId = owningSectionId(routes, location.pathname);

  /* The rail joins the sticky ledger: it stamps its rendered height on the
   * root as --apf-section-rail-clearance, and --apf-sticky-offset adds it to
   * the measured header height. Sticky asides, scroll margins and the global
   * scroll padding all read the sum, so hash targets land below the whole
   * sticky stack, and pages without a rail pay no phantom clearance. */
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    const root = document.documentElement;
    const apply = () => {
      root.style.setProperty("--apf-section-rail-clearance", `${Math.ceil(rail.getBoundingClientRect().height)}px`);
    };
    apply();
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(apply) : undefined;
    observer?.observe(rail);
    return () => {
      observer?.disconnect();
      root.style.removeProperty("--apf-section-rail-clearance");
    };
  }, [sectionId]);

  if (!sectionId) return null;
  const sectionRoute = routes.find((route) => route.id === sectionId);
  if (!sectionRoute) return null;

  const entries = routes
    .flatMap((route) => {
      const placement = route.sectionNavigation?.find(
        (candidate) => candidate.sectionId === sectionId,
      );
      return placement ? [{ route, order: placement.order }] : [];
    })
    .sort((left, right) => left.order - right.order);
  if (entries.length < 2) return null;

  return (
    <nav
      ref={railRef}
      className="apf-section-navigation"
      aria-label={`${copy(sectionRoute.labelKey)} ${copy("nav.section-label", "section").toLocaleLowerCase()}`}
    >
      <ul className="apf-section-navigation__list">
        {entries.map(({ route }) => {
          /* An entry claims exact-match marking only when another entry is
           * nested beneath it; otherwise its detail routes keep it current. */
          const hasNestedEntry = entries.some(
            (candidate) =>
              candidate.route !== route &&
              candidate.route.path.startsWith(`${route.path}/`),
          );
          return (
            <li className="apf-section-navigation__item" key={route.id}>
              <NavLink
                to={route.path}
                end={hasNestedEntry}
                className={({ isActive }) =>
                  `apf-section-navigation__link${isActive ? " apf-section-navigation__link--current" : ""}`
                }
              >
                {copy(route.labelKey)}
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
